// Read-only Continuum internal dashboard at /app.
// Surfaces:
//   - Tenant summary + active method version
//   - Intake activity (waitlist, walkthrough requests, community questions)
//   - Demo tenant evidence base (communities, charities by sector, indicators, disparity)

import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  Building2, Users, MapPin, BarChart3, GitBranch, RefreshCw, LogOut,
  Mail, ClipboardList, MessageSquare, AlertTriangle, Loader2,
} from "lucide-react";
import {
  getAdminKey, setAdminKey,
  fetchAdminSummary, fetchAdminWaitlist, fetchAdminWalkthrough, fetchAdminQuestions,
  fetchCommunityBundle, reseedDemo,
} from "@/lib/platformApi";
import ContinuumBand from "@/components/ContinuumBand";

const SECTOR_COLOR = {
  housing: "#0A1224", homelessness: "#3B4C68", mental_health: "#8898AA",
  addiction: "#D9A05B", community: "#FF8A00",
};
const SECTOR_LABEL = {
  housing: "Housing", homelessness: "Homelessness", mental_health: "Mental health",
  addiction: "Addiction", community: "Community",
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-CA", {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "2-digit" }); }
  catch { return iso.slice(0, 10); }
};

function StatBlock({ label, value, sub, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 p-6 border-l-4 border-l-[#FF8A00]">
      <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">{label}</div>
      <div className="text-3xl md:text-4xl font-black tabular-nums text-[#0A1224] tracking-tight mt-2">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1 tabular-nums">{sub}</div>}
    </div>
  );
}

function SectionCard({ title, icon: Icon, action, testid, children }) {
  return (
    <section data-testid={testid} className="bg-white border border-slate-200">
      <header className="px-6 py-4 flex items-center justify-between border-b border-slate-200">
        <div className="flex items-center gap-3">
          {Icon && <Icon size={18} className="text-[#FF8A00]" />}
          <h2 className="font-black tracking-tight text-[#0A1224]">{title}</h2>
        </div>
        {action}
      </header>
      <div>{children}</div>
    </section>
  );
}

function Empty({ children }) {
  return <p className="text-slate-500 text-sm italic px-6 py-6">{children}</p>;
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const hasKey = Boolean(getAdminKey());
  const [summary, setSummary] = useState(null);
  const [waitlist, setWaitlist] = useState([]);
  const [walkthrough, setWalkthrough] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [activeCommunity, setActiveCommunity] = useState(null);
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reseeding, setReseeding] = useState(false);
  const [err, setErr] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    setErr(null);
    try {
      const [s, w1, w2, q] = await Promise.all([
        fetchAdminSummary(),
        fetchAdminWaitlist(),
        fetchAdminWalkthrough(),
        fetchAdminQuestions(),
      ]);
      setSummary(s);
      setWaitlist(w1);
      setWalkthrough(w2);
      setQuestions(q);
      if (s.communities?.length) {
        const slug = activeCommunity || s.communities[0].slug;
        setActiveCommunity(slug);
        const b = await fetchCommunityBundle(slug);
        setBundle(b);
      }
    } catch (e) {
      if (e?.response?.status === 401) {
        setAdminKey("");
        navigate("/app/login", { replace: true });
        return;
      }
      setErr(e?.response?.data?.detail || e.message || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasKey) return;
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasKey]);

  useEffect(() => {
    if (!hasKey || !activeCommunity) return;
    (async () => {
      try {
        const b = await fetchCommunityBundle(activeCommunity);
        setBundle(b);
      } catch (e) {
        setErr(e?.response?.data?.detail || e.message);
      }
    })();
  }, [activeCommunity, hasKey]);

  if (!hasKey) return <Navigate to="/app/login" replace />;

  const handleReseed = async () => {
    if (!window.confirm("Reseed the demo data? This will wipe & reinsert communities, charities, indicators, and disparity. Intake (waitlist/walkthrough/questions) is NOT touched.")) return;
    setReseeding(true);
    try {
      await reseedDemo();
      await loadAll();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setReseeding(false);
    }
  };

  const handleSignOut = () => {
    setAdminKey("");
    navigate("/app/login", { replace: true });
  };

  return (
    <div className="bg-[#F8F9FA] min-h-screen">
      <ContinuumBand className="h-1" />
      <header className="bg-[#0A1224] text-white" data-testid="admin-header">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">Continuum · Internal dashboard</div>
            <h1 className="text-2xl md:text-3xl font-black tracking-tighter mt-1">Evidence base & intake</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-reseed"
              onClick={handleReseed}
              disabled={reseeding}
              className="border border-white/30 text-white hover:bg-white hover:text-[#0A1224] px-4 py-2 text-sm font-bold inline-flex items-center gap-2 transition-colors disabled:opacity-60"
            >
              {reseeding ? <Loader2 className="animate-spin" size={14}/> : <RefreshCw size={14}/>}
              Reseed demo
            </button>
            <button
              data-testid="admin-signout"
              onClick={handleSignOut}
              className="text-slate-300 hover:text-white px-3 py-2 text-sm inline-flex items-center gap-2"
            >
              <LogOut size={14}/> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        {err && (
          <div className="border border-amber-500 bg-amber-50 p-5 mb-8 flex items-start gap-3" data-testid="admin-error">
            <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={18} />
            <div>
              <div className="font-bold text-[#0A1224]">Couldn't load dashboard data</div>
              <p className="text-slate-700 text-sm mt-1">{err}</p>
            </div>
          </div>
        )}

        {loading && !summary ? (
          <div className="py-32 flex items-center justify-center text-slate-500"><Loader2 className="animate-spin" /></div>
        ) : (
          summary && (
            <>
              {/* Top stat blocks */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <StatBlock testid="stat-communities" label="Communities" value={summary.communities.length} sub="demo tenant" />
                <StatBlock testid="stat-charities" label="Charities" value={summary.total_charities} sub="CRA register · demo seed" />
                <StatBlock testid="stat-indicators" label="Indicators" value={summary.total_indicators} sub="StatsCan · demo seed" />
                <StatBlock testid="stat-method" label="Method version" value={summary.method_version.version} sub={`active since ${fmtDate(summary.method_version.effective_from)}`} />
              </div>

              {/* Intake panels */}
              <div className="grid lg:grid-cols-3 gap-4 mb-10">
                <SectionCard testid="card-waitlist" title={`Waitlist (${waitlist.length})`} icon={Mail}>
                  {waitlist.length === 0 ? <Empty>No submissions yet.</Empty> : (
                    <ul className="divide-y divide-slate-100">
                      {waitlist.slice(0, 6).map((w) => (
                        <li key={w.id} className="px-6 py-4">
                          <div className="font-bold text-[#0A1224] text-sm">{w.name || w.email}</div>
                          <div className="text-xs text-slate-500 truncate">{w.email}</div>
                          {w.organization && <div className="text-xs text-slate-700 mt-1">{w.organization}{w.audience ? ` · ${w.audience}` : ""}</div>}
                          <div className="text-[11px] text-slate-400 tabular-nums mt-1">{fmtDateTime(w.created_at)}</div>
                        </li>
                      ))}
                    </ul>
                  )}
                </SectionCard>

                <SectionCard testid="card-walkthrough" title={`Walkthrough (${walkthrough.length})`} icon={ClipboardList}>
                  {walkthrough.length === 0 ? <Empty>No walkthrough requests yet.</Empty> : (
                    <ul className="divide-y divide-slate-100">
                      {walkthrough.slice(0, 6).map((w) => (
                        <li key={w.id} className="px-6 py-4">
                          <div className="font-bold text-[#0A1224] text-sm">{w.name}</div>
                          <div className="text-xs text-slate-700">{w.organization}{w.role ? ` · ${w.role}` : ""}</div>
                          {w.community && <div className="text-xs text-slate-500 mt-1">📍 {w.community}</div>}
                          <div className="text-[11px] text-slate-400 tabular-nums mt-1">{fmtDateTime(w.created_at)}</div>
                        </li>
                      ))}
                    </ul>
                  )}
                </SectionCard>

                <SectionCard testid="card-questions" title={`Community questions (${questions.length})`} icon={MessageSquare}>
                  {questions.length === 0 ? <Empty>No questions yet.</Empty> : (
                    <ul className="divide-y divide-slate-100">
                      {questions.slice(0, 6).map((q) => (
                        <li key={q.id} className="px-6 py-4">
                          <div className="font-bold text-[#0A1224] text-sm">{q.community}</div>
                          {q.question && <div className="text-xs text-slate-700 mt-1 italic">"{q.question}"</div>}
                          <div className="text-xs text-slate-500 truncate mt-1">{q.email}</div>
                          <div className="text-[11px] text-slate-400 tabular-nums mt-1">{fmtDateTime(q.created_at)}</div>
                        </li>
                      ))}
                    </ul>
                  )}
                </SectionCard>
              </div>

              {/* Evidence base for selected community */}
              <div className="grid lg:grid-cols-12 gap-6">
                {/* Community selector */}
                <aside className="lg:col-span-3">
                  <SectionCard testid="card-communities" title="Communities" icon={MapPin}>
                    <ul>
                      {summary.communities.map((c) => (
                        <li key={c.slug}>
                          <button
                            data-testid={`admin-community-${c.slug}`}
                            onClick={() => setActiveCommunity(c.slug)}
                            className={`w-full text-left px-6 py-4 border-l-4 transition-colors ${
                              c.slug === activeCommunity
                                ? "border-[#FF8A00] bg-[#FF8A00]/5"
                                : "border-transparent hover:bg-slate-50"
                            }`}
                          >
                            <div className="font-bold text-[#0A1224] text-sm">{c.name}</div>
                            <div className="text-xs text-slate-500 tabular-nums">
                              pop {c.population?.toLocaleString() || "—"}
                            </div>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </SectionCard>
                </aside>

                {/* Bundle detail */}
                <div className="lg:col-span-9 space-y-6">
                  {bundle ? (
                    <>
                      <SectionCard
                        testid="card-evidence-charities"
                        title={`${bundle.community.name} · Charities (${bundle.charities.length})`}
                        icon={Building2}
                      >
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-left border-b-2 border-[#FF8A00]">
                                <th className="px-6 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-500">Charity</th>
                                <th className="px-6 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-500">Sector</th>
                                <th className="px-6 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-500">Designation</th>
                                <th className="px-6 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-500">Lineage</th>
                              </tr>
                            </thead>
                            <tbody>
                              {bundle.charities.map((c) => (
                                <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50">
                                  <td className="px-6 py-3 font-medium text-[#0A1224]">{c.legal_name}</td>
                                  <td className="px-6 py-3">
                                    <span className="inline-flex items-center gap-2 text-xs">
                                      <span className="w-2 h-2 inline-block" style={{ background: SECTOR_COLOR[c.sector] }} />
                                      {SECTOR_LABEL[c.sector]}
                                    </span>
                                  </td>
                                  <td className="px-6 py-3 text-slate-600 text-xs">{c.designation || "—"}</td>
                                  <td className="px-6 py-3 text-xs text-slate-500">
                                    <div className="font-mono">{c.method_version}</div>
                                    <div className="tabular-nums">{fmtDate(c.lineage.refreshed_at)}</div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </SectionCard>

                      <div className="grid md:grid-cols-2 gap-6">
                        <SectionCard testid="card-indicators" title="Indicators" icon={BarChart3}>
                          <ul className="divide-y divide-slate-100">
                            {bundle.indicators.map((i) => (
                              <li key={i.id} className="px-6 py-4">
                                <div className="flex items-baseline justify-between gap-4">
                                  <div className="text-sm font-bold text-[#0A1224]">{i.label}</div>
                                  <div className="text-2xl font-black tabular-nums text-[#0A1224]">
                                    {i.value}{i.unit === "%" ? "%" : ""} {i.unit !== "%" && <span className="text-xs font-medium text-slate-500">{i.unit}</span>}
                                  </div>
                                </div>
                                <div className="text-xs text-slate-500 tabular-nums mt-1">
                                  {i.year ? `${i.year} · ` : ""}
                                  <span className="font-mono">{i.method_version}</span> · refreshed {fmtDate(i.lineage.refreshed_at)}
                                </div>
                                {i.lineage.note && (
                                  <p className="text-[11px] text-slate-500 italic mt-1 leading-relaxed">{i.lineage.note}</p>
                                )}
                              </li>
                            ))}
                          </ul>
                        </SectionCard>

                        <SectionCard testid="card-disparity" title="Disparity (v0.1)" icon={GitBranch}>
                          <ul className="divide-y divide-slate-100">
                            {bundle.disparity.map((d) => (
                              <li key={`${d.community_slug}-${d.sector}`} className="px-6 py-4">
                                <div className="flex items-center justify-between gap-3">
                                  <div className="text-sm font-bold text-[#0A1224] flex items-center gap-2">
                                    <span className="w-2.5 h-2.5 inline-block" style={{ background: SECTOR_COLOR[d.sector] }} />
                                    {SECTOR_LABEL[d.sector]}
                                  </div>
                                  <div className="text-lg font-black tabular-nums text-[#0A1224]">
                                    {d.disparity_score.toFixed(2)}
                                  </div>
                                </div>
                                <div className="text-xs text-slate-500 tabular-nums mt-1">
                                  {d.inflow_cad.toFixed(0)} agencies · {d.inflow_per_capita_cad.toFixed(2)} per 100k pop · need {d.need_value}%
                                </div>
                              </li>
                            ))}
                          </ul>
                        </SectionCard>
                      </div>
                    </>
                  ) : (
                    <SectionCard testid="card-evidence-empty" title="Evidence base">
                      <Empty>Select a community to view its evidence base.</Empty>
                    </SectionCard>
                  )}
                </div>
              </div>

              <footer className="mt-12 pt-8 border-t border-slate-200 text-xs text-slate-500 tabular-nums" data-testid="admin-footer">
                Method <span className="font-mono">{summary.method_version.version}</span> · last refresh {fmtDateTime(summary.last_refresh)} · data residency: Canadian regions
              </footer>
            </>
          )
        )}
      </main>
    </div>
  );
}
